import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCreateCoverDesignMutation } from '../../../redux/api/fymAPI';
import type { CatalogItem } from '../../../redux/api/fymAPI.types';
import { coverEditorPath } from '../coverEditorPath';
import { useCoverPictureUpload } from '../panels/picture/useReferenceUpload';
import { isLocalUpload, withLocalUpload } from '../publicMode/publicCoverData';
import { browserCoverStorage, writeCarry } from '../publicMode/publicCoverStore';
import { carriedDocForSong } from './carriedDoc';
import type { CarriedDesign } from './useCarriedDesign';

/**
 * Puts a carried design on a song (design g4): an own picture of the visitor goes up to the
 * account first (it was only in the browser), the title and artist boxes take the song's text,
 * the design becomes a new version of the song (`POST .../designs`), and the editor of the song
 * opens on it. The carry is cleared; the design of this browser stays for the public tool.
 */
export function usePutCarriedDesign(accountId: number, carried: CarriedDesign | null | undefined) {
  const navigate = useNavigate();
  const [createDesign] = useCreateCoverDesignMutation();
  const upload = useCoverPictureUpload(accountId);
  const [busyUuid, setBusyUuid] = useState<string | null>(null);
  const [isFailed, setIsFailed] = useState(false);

  const put = useCallback(
    async (song: CatalogItem): Promise<void> => {
      if (carried === null || carried === undefined) {
        return;
      }
      setBusyUuid(song.uuid);
      setIsFailed(false);
      try {
        let { doc } = carried;
        if (isLocalUpload(doc.background) && doc.background.kind === 'picture') {
          const file =
            carried.upload === null ? null : new File([carried.upload], 'cover-picture', { type: carried.upload.type });
          const saved = file === null ? null : await upload.uploadPicture(file);
          doc =
            saved === null
              ? withLocalUpload(doc, null).doc
              : {
                  ...doc,
                  background: { ...doc.background, source: { kind: 'upload', fileId: saved.fileId }, url: saved.url },
                };
        }
        await createDesign({ accountId, catalogItemUuid: song.uuid, doc: carriedDocForSong(doc, song) }).unwrap();
        writeCarry(browserCoverStorage(), null);
        // eslint-disable-next-line no-void -- a router navigation: nothing to wait for
        void navigate(coverEditorPath(song.uuid, { accountId }), { replace: true });
      } catch (error) {
        console.error('Cover maker: the design did not go to the song', error);
        setIsFailed(true);
        setBusyUuid(null);
      }
    },
    [accountId, carried, createDesign, navigate, upload]
  );

  return { put, busyUuid, isFailed };
}
